import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Configure axios defaults
axios.defaults.withCredentials = true;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function parseAuthError(e) {
  if (e.response?.data?.detail) {
    return formatApiErrorDetail(e.response.data.detail);
  }
  if (e.code === 'ERR_NETWORK' || !e.response) {
    return "Cannot reach the server. Please check your connection or try again later.";
  }
  return e.message || "Something went wrong. Please try again.";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        headers
      });
      setUser(data);
    } catch {
      // Try refresh token
      try {
        const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token);
          const userRes = await axios.get(`${API_URL}/api/auth/me`, {
            withCredentials: true,
            headers: { Authorization: `Bearer ${data.access_token}` }
          });
          setUser(userRes.data);
        }
      } catch {
        setUser(false);
        localStorage.removeItem('access_token');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: parseAuthError(e) };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: parseAuthError(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error', e);
    }
    localStorage.removeItem('access_token');
    setUser(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  }, []);

  const value = useMemo(() => ({
    user, loading, login, register, logout, updateUser, checkAuth
  }), [user, loading, login, register, logout, updateUser, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
